import {
  AreaUnitEnum,
  CONVERSION_UNIT_ABBREVIATION,
  ConversionUnitSchema,
  type ConversionUnit,
  type ItemUnit,
  type ItemUnitConversion,
} from "@forgepoint/shared";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DECIMAL_INPUT = /^(\d+\.?\d*|\.\d+)$/;

interface ItemUnitConversionsEditorProps {
  value: ItemUnitConversion[];
  onChange: (next: ItemUnitConversion[]) => void;
  unitOptions: ConversionUnit[];
  itemUnit: ItemUnit;
  disabled?: boolean;
}

/**
 * Edits an item's unit conversion rates, one "1 [unit] = [rate] [unit]" row
 * per rate. A new row starts from the item unit.
 */
export function ItemUnitConversionsEditor({
  value,
  onChange,
  unitOptions,
  itemUnit,
  disabled = false,
}: ItemUnitConversionsEditorProps) {
  const updateRow = (index: number, patch: Partial<ItemUnitConversion>) =>
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const handleUnitChange = (
    index: number,
    side: "fromUnit" | "toUnit",
    unit: string,
  ) => {
    const parsed = ConversionUnitSchema.safeParse(unit);
    if (!parsed.success) {
      return;
    }
    updateRow(
      index,
      side === "fromUnit" ? { fromUnit: parsed.data } : { toUnit: parsed.data },
    );
  };

  const handleRateChange = (index: number, rate: string) => {
    if (rate === "" || DECIMAL_INPUT.test(rate)) {
      updateRow(index, { rate });
    }
  };

  const handleAdd = () =>
    onChange([
      ...value,
      { fromUnit: itemUnit, toUnit: AreaUnitEnum.square_feet, rate: "" },
    ]);

  const handleRemove = (index: number) =>
    onChange(value.filter((_, i) => i !== index));

  const unitItems = unitOptions.map((unit) => (
    <SelectItem key={unit} value={unit}>
      {CONVERSION_UNIT_ABBREVIATION[unit]}
    </SelectItem>
  ));

  return (
    <div className="py-2.5 space-y-2" data-field-anchor="unitConversions">
      <div className="flex items-center justify-between gap-4">
        <Label className="text-sm font-medium leading-tight">
          Unit conversions
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={disabled}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add conversion
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Convert between cost, selling and measurement units, such as 1 box = 12
        each. Measurements describe one each; packaging defaults to 1:1 when no
        conversion is available.
      </p>
      {value.map((conversion, index) => (
        <div key={index} className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">1</span>
            <Select
              disabled={disabled}
              value={conversion.fromUnit}
              onValueChange={(unit) =>
                handleUnitChange(index, "fromUnit", unit)
              }
            >
              <SelectTrigger className="w-24" aria-label="From unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>{unitItems}</SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">=</span>
          </div>
          <div className="flex items-center gap-2">
            <Input
              disabled={disabled}
              type="text"
              inputMode="decimal"
              placeholder="Rate"
              aria-label="Rate"
              value={conversion.rate}
              onChange={(e) => handleRateChange(index, e.target.value)}
              className="w-28"
            />
            <Select
              disabled={disabled}
              value={conversion.toUnit}
              onValueChange={(unit) => handleUnitChange(index, "toUnit", unit)}
            >
              <SelectTrigger className="w-24" aria-label="To unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>{unitItems}</SelectContent>
            </Select>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled}
              aria-label="Remove conversion"
              onClick={() => handleRemove(index)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
